import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import router from "@/router";
import { getModule } from "vuex-module-decorators";
import AuthModule from "@/store/modules/core/auth.module";
import store from "@/store";
import SnackbarModule from "@/store/modules/core/snackbar.module";
import OverlayModule from "@/store/modules/core/overlay.module";
import { ApiEndpoint, endpointDict } from "./api.enum";
import AbortRequestsModule from "@/store/modules/abort-requests.module";
const authModule = getModule(AuthModule, store);
const snackbarModule = getModule(SnackbarModule, store);
const overlayModule = getModule(OverlayModule, store);
const abortRequestsModule = getModule(AbortRequestsModule, store);

axios.interceptors.request.use(
  async (config) => {
    config.withCredentials = true;

    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error.response && error.response.status) {
      switch (error.response.status) {
        case 403:
          overlayModule.hideOverlay();
          snackbarModule.showSnackbar({
            message: "Errore " + error.response.status + ": Permesso negato",
            type: "error",
          });
          setTimeout(() => snackbarModule.hideSnackbar(), 5000);
          break;
        case 401:
          if (router.currentRoute.path !== "/login") {
            overlayModule.hideOverlay();
            authModule.logout();
            router.push({ name: "Login" });
          }
          break;
        case 400:
          overlayModule.hideOverlay();
          if (router.currentRoute.path !== "/login") {
            /* Bad Request */
            snackbarModule.showSnackbar({
              message:
                "Errore " + error.response.status + ": " + error.response.data,
              type: "error",
            });
            setTimeout(() => snackbarModule.hideSnackbar(), 5000);
          }
          break;
        default:
          overlayModule.hideOverlay();
          snackbarModule.showSnackbar({
            message:
              "Errore " + error.response.status + ": " + error.response.data,
            type: "error",
          });
          setTimeout(() => snackbarModule.hideSnackbar(), 5000);
      }
    } else {
      overlayModule.hideOverlay();
      if (error.message) {
        snackbarModule.showSnackbar({
          message: "Si è verificato un errore: " + error.message,
          type: "error",
        });
        setTimeout(() => snackbarModule.hideSnackbar(), 5000);
        // } else if (error.hasOwnProperty("response")) {
        //    snackbarModule.showSnackbar({
        //      message: "Si è verificato un errore." +  error.response.hasOwnProperty("data") ? error.response.data : "",
        //      type: "error",
        //    });
        //    setTimeout(() => snackbarModule.hideSnackbar(), 5000);
      } else {
        snackbarModule.showSnackbar({
          message: "Si è verificato un errore.",
          type: "error",
        });
        setTimeout(() => snackbarModule.hideSnackbar(), 5000);
      }
    }
    return Promise.reject(error);
  }
);

export abstract class ApiService {
  constructor(endpoint?: ApiEndpoint) {
    axios.defaults.baseURL =
      endpointDict[endpoint ? endpoint : ApiEndpoint.STANDARD];
    axios.defaults.headers.common["Access-Control-Allow-Origin"] = "*";
  }

  getAll<ResponseType>(
    uri: string,
    params: AxiosRequestConfig = null
  ): Promise<AxiosResponse<ResponseType>> {
    if (!params || !params.signal) {
      const source = new AbortController();
      abortRequestsModule.push(source);
      if (!params) params = {} as AxiosRequestConfig;
      params.signal = source.signal;
    }
    return axios.get<ResponseType>(uri, params);
  }

  getByID<IdentifierType, ResponseType>(
    uri: string,
    id: IdentifierType,
    params: AxiosRequestConfig = null
  ): Promise<AxiosResponse<ResponseType>> {
    return axios.get<ResponseType>(`${uri}/${id}`, params);
  }

  post<BodyType, ResponseType>(
    uri: string,
    params: BodyType
  ): Promise<AxiosResponse<ResponseType>> {
    return axios.post<ResponseType>(`${uri}`, params);
  }

  delete<IdentifierType, ResponseType>(
    uri: string,
    id: IdentifierType
  ): Promise<AxiosResponse<ResponseType>> {
    return axios.delete(`${uri}/${id}`);
  }

  upload<ResponseType>(
    uri: string,
    formdata: FormData
  ): Promise<AxiosResponse<ResponseType>> {
    return axios.post(`${uri}`, formdata, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  patch<BodyType, ResponseType>(
    uri: string,
    params: BodyType
  ): Promise<AxiosResponse<ResponseType>> {
    return axios.patch(`${uri}`, params);
  }

  update<BodyType, ResponseType>(
    uri: string,
    params: BodyType
  ): Promise<AxiosResponse<ResponseType>> {
    return axios.put(`${uri}`, params);
  }

  updateById<IdentifierType, BodyType, ResponseType>(
    uri: string,
    id: IdentifierType,
    params: BodyType
  ): Promise<AxiosResponse<ResponseType>> {
    return axios.put(`${uri}/${id}`, params);
  }
}
